import { useState, useCallback } from 'react';

type ValueSetter<T> = (
  value: T | undefined | ((storedValue: T | undefined) => T | undefined)
) => void;

/**
 * Stores data in local storage given a key
 * @param key Key that will store data in local storage
 * @param initialValue Default value for the key
 * @returns a tuple with the current stored value and a callback to set a new value
 * @example
 * import { useLocalState } from '@adsk/alloy-react-helpers';
 *
 * const [param, setParam] = useLocalState(storageKey, 1);
 * @see Table
 */
export default function useLocalState<T>(
  key?: string,
  initialValue?: T
): [T | undefined, ValueSetter<T>] {
  if (!key) {
    throw new Error('useLocalState key may not be falsy');
  }

  const [storedValue, setStoredValue] = useState<T | undefined>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log(error);
      return initialValue;
    }
  });

  const setValue: ValueSetter<T> = useCallback(
    (value) => {
      setStoredValue((prevStoredValue) => {
        try {
          const valueToStore =
            value instanceof Function ? value(prevStoredValue) : value;
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
          return valueToStore;
        } catch (error) {
          console.log(error);
          return prevStoredValue;
        }
      });
    },
    [key]
  );

  return [storedValue, setValue];
}
